import { useEffect, useRef, useState } from 'react'
import { FieldArray } from 'formik'
import { Button } from 'ui'
import { PremiumRangeByDay } from './PremiumRangeByDay'
import { PremiumRangeByHour } from './PremiumRangeByHour'
import { PremiumRangeByDayMobile } from './PremiumRangeByDayMobile'
import { PremiumRangeByHourMobile } from './PremiumRangeByHourMobile'

import { t } from 'translations'
import { RangeByType } from './types'

interface WagePremiumRangeProps {
  values: RangeByType[]
  setFieldValue: <T>(field: T) => void
  setFieldTouched: (field: string, isTouched: boolean) => void
  isSubmitting: boolean
  isValidating: boolean
  errors: boolean
}

const breakpoint = 800

const WagePremiumRange: React.FC<WagePremiumRangeProps> = ({ values, setFieldValue, setFieldTouched, isSubmitting, isValidating, errors }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<number>(0)
  const [isInitialized, setIsinitialized] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      if (!ref.current) return
      setWidth(ref.current.offsetWidth)
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  })

  useEffect(() => {
    if (isSubmitting && !isValidating && values.length > 0) {
      for (const index in values) {
        setFieldTouched(`assignBasedOnTheseRanges[${index}].startTime`, true)
        setFieldTouched(`assignBasedOnTheseRanges[${index}].endTime`, true)
      }
    }
  }, [isSubmitting, isValidating, setFieldTouched, values])

  return (
    <div ref={ref}>
      <FieldArray
        name="assignBasedOnTheseRanges"
        render={arrayHelpers => {
          if (!isInitialized) {
            if (values.length === 0) arrayHelpers.push({ type: 'byDay' })
            setIsinitialized(true)
          }
          return (
            <div className="ml-4" style={{ maxWidth: 815 }}>
              {values
                ? values.map((value, index) => (
                    <div key={index}>
                      {value.type === 'byDay' ? (
                        <>
                          {width < breakpoint ? (
                            <PremiumRangeByDayMobile
                              setFieldTouched={setFieldTouched}
                              isSubmitting={isSubmitting}
                              isValidating={isValidating}
                              value={value}
                              index={index}
                              arrayHelpers={arrayHelpers}
                            />
                          ) : (
                            <PremiumRangeByDay
                              setFieldTouched={setFieldTouched}
                              isSubmitting={isSubmitting}
                              isValidating={isValidating}
                              value={value}
                              index={index}
                              arrayHelpers={arrayHelpers}
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {width < breakpoint ? (
                            <PremiumRangeByHourMobile
                              setFieldValue={setFieldValue}
                              setFieldTouched={setFieldTouched}
                              isSubmitting={isSubmitting}
                              isValidating={isValidating}
                              value={value}
                              index={index}
                              arrayHelpers={arrayHelpers}
                            />
                          ) : (
                            <PremiumRangeByHour
                              setFieldValue={setFieldValue}
                              setFieldTouched={setFieldTouched}
                              isSubmitting={isSubmitting}
                              isValidating={isValidating}
                              value={value}
                              index={index}
                              arrayHelpers={arrayHelpers}
                            />
                          )}
                        </>
                      )}
                      {width < breakpoint ? <hr /> : null}
                    </div>
                  ))
                : null}
              <Button
                className={`mb-4 ${width < breakpoint ? 'w-100' : ''}`}
                color="light"
                onClick={event => {
                  event.preventDefault()
                  arrayHelpers.push({ type: 'byDay' })
                }}
              >
                <i className="fas fa-clock mr-1" />
                {t('administration.wagePremiums.form.ADD_RANGE')}
              </Button>
            </div>
          )
        }}
      />
      {errors ? <div className="mb-4 text-danger">{t(`administration.wagePremiums.form.MIN_ONE_RANGE`)}</div> : null}
    </div>
  )
}

export default WagePremiumRange
